import { Box, Stack } from "@mui/material";
import { DamsAppAsset } from "../../assets/registry";
import { MainPageBase } from "../page-bases/MainPageBase";
import { ChangeEvent, useEffect, useRef, useState } from "react";

import DamsTable from "../../components/Table/CollapsibleTable";
import { ContainedButton } from "../../components/Button/AppButton";
import { NoItemContent } from "../page-bases/NoItemContent";
import { TRowItem } from "../../components/Table/TableRow";
import { ModalBase } from "../../components/Modals/ModalBase";
import ModalItemInput from "../../components/Input/ModalItemInput";
import { AssetCategoryApi } from "../../services/assets/assetCategory";
import {
  AssetCategory,
  AssetCategoryDetail,
  AssetCategoryType,
  IPagination,
  IQueryPerams,
  PageActions,
} from "../../config/types";
import { usePageModels } from "../../hooks/usePageActions";
import { Field, FormField } from "../../utils/formSchema";
import useInputUpdateHelper from "../../hooks/useInputHelper";
import { useFieldHasError } from "../../hooks/useFieldHasError";
import ItemSelectInput from "../../components/Input/ItemSelectInput";
import useSelectHelper from "../../hooks/useSelectHelper";
import { isFormFilled } from "../../utils/validators";
import { BadgeLabel } from "../../components/Label/BadgeLabel";
import { useMangeTableItems } from "../../hooks/useManageTableItems";
import { usePagination } from "../../hooks/usePagination";
import { BulkDeleteController } from "../../common-ui/BulkDeleteController";
import SimpleLabel from "../../components/Label";
import { actionDialogStore, alertDialogStore } from "../../store/dialogs";
import { formatDate } from "../../utils/common";
import { AssetCategoryFilter } from "../../common-ui/page-filters/AssetCategoryFilter";
import { AssetTagSelectInput } from "../../common-ui/select-input-models/AssetTagSelectInput";
import { AssetTagApi } from "../../services/assets/assetTag";

interface AssetCategoryT extends FormField<{
  name: string;
  asset_type: AssetCategoryType | "";
}> {
  submitAttempt?: boolean;
}

const __NEW_ASSET_CATE_DEFAULT_VALUE: AssetCategoryT = {
  name: { isValid: false, value: "", errorMessage: "required" },
  asset_type: { isValid: false, value: "", errorMessage: "required" },
};

interface IAssetTag {
  id: string;
  name: string;
}

function AssetCategoriesMainPage() {
  const [pageState, setPageState] = useState("main");
  const [dataset, setDataset] = useState<TRowItem[]>([]);
  const [form, updateForm] = useState<AssetCategoryT>(
    __NEW_ASSET_CATE_DEFAULT_VALUE,
  );

  const [loading, setLoading] = useState(false);
  const [pageAction, updatePageAction] = useState<PageActions>();
  const { models, closeModel, openModel } = usePageModels();
  const [isProcessing, setProcess] = useState(false);
  const isCategoryTagForm = useRef<boolean>(false);
  const [AssetCateform, updateAssetCateform] = useState<AssetCategoryT>(
    __NEW_ASSET_CATE_DEFAULT_VALUE,
  );
  const [assetTag, updateAssetTag] = useState<string>("");
  const [AssetTagList, updateAssetTagList] = useState<{
    submitAttempt?: boolean;
    data: string[];
  }>({ data: [] });
  const [previousTagsCart, setPreviousTagsCart] = useState<IAssetTag[]>([]);

  const [querySet, setQuerySet] = useState<IQueryPerams>({ is_deleted: false });
  const [selectedAssetTags, setSelectedAssetTags] = useState<TRowItem[]>([]);

  const {
    selectedRowItems,
    toggleRow,
    toggleSelectAllRow,
    resetSelectedRowItems,
  } = useMangeTableItems(dataset);

  const {
    pagination,
    paginateToNextPage,
    paginateToPreviousPage,
    paginateToPageNumber,
    updatePaginationInitState,
  } = usePagination<AssetCategoryDetail[]>(AssetCategoryApi.getAssetCategories);

  const fetchData = async () => {
    setLoading(true);
    const result = await AssetCategoryApi.getAssetCategories(querySet);
    if (result?.data) {
      populateDataSet(result.data, result.pagination);
    }
    setLoading(false);
  };

  const populateDataSet = (
    data: AssetCategoryDetail[],
    pagination?: IPagination,
  ) => {
    const formatData = data.map((data) => ({
      ...data,
      created_at: formatDate(data.created_at),
    }));
    setDataset(formatData);
    updatePaginationInitState(pagination);
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [querySet]);

  function updateTagList() {
    if (assetTag === "") {
      return;
    }
    updateAssetTagList((prev) => {
      if (prev.data.includes(assetTag)) {
        return prev;
      }
      return { ...prev, data: [...prev.data, assetTag] };
    });
  }

  function removeTag(tagToRemove: string) {
    updateAssetTagList((prev) => ({
      ...prev,
      data: prev.data.filter((tag) => tag !== tagToRemove),
    }));
  }

  const handleAssetCateInputUpdate =
    useInputUpdateHelper<AssetCategoryT>(updateAssetCateform);
  const handleSelectUpdate =
    useSelectHelper<AssetCategoryT>(updateAssetCateform);
  const AssetCateFormHasError = useFieldHasError<AssetCategoryT>(AssetCateform);

  const page_info = getEmptyPageInfo(pageState);

  async function handleCategoryRequest() {
    updateAssetCateform((prev) => ({ ...prev, submitAttempt: true }));
    const formValid = isFormFilled<AssetCategoryT>(AssetCateform);
    if (formValid) {
      setProcess(true);
      const data = {
        name: AssetCateform.name.value,
        asset_type: AssetCateform.asset_type?.value as AssetCategoryType,
      };
      const ftn =
        pageAction?.type === "edit"
          ? AssetCategoryApi.updateAssetCategory(pageAction?.id ?? "", data)
          : AssetCategoryApi.createAssetCategory(data);

      const res = await ftn;
      if (res && res.success) {
        await fetchData();
        handleModelClose();
      }
      setProcess(false);
      alertDialogStore.open({
        subtitle: res?.message,
        title: `${
          pageAction?.type === "edit" ? "Update" : "New"
        } Asset Category`,
        isSuccess: res?.success,
      });
      updateAssetCateform(__NEW_ASSET_CATE_DEFAULT_VALUE);
    }
  }

  function handleAssetTagSelect(items: TRowItem[]) {
    setSelectedAssetTags(items);
    updateAssetTagList((prev) => ({
      ...prev,
      data: items.map((item) => item.name),
    }));
  }

  async function handleAddAssetTagRequest() {
    updateAssetTagList((prev) => ({ ...prev, submitAttempt: true }));
    const formValid = selectedAssetTags.length > 0;
    if (formValid && pageAction?.id) {
      setProcess(true);
      const res = await AssetCategoryApi.createAssetCategoryTag({
        asset_category_id: pageAction?.id,
        asset_tag_ids: selectedAssetTags.map((tag) => tag.id),
      });
      if (res && res.success) {
        await fetchData();
        await fetchCategoryTags(pageAction?.id);
        handleModelClose();
      }
      setProcess(false);
      alertDialogStore.open({
        subtitle: res?.message,
        title: "New Asset Tags",
        isSuccess: res?.success,
      });
      setSelectedAssetTags([]);
      updateAssetTagList({ data: [] });
    }
  }

  function handleTagInput(event: ChangeEvent<HTMLInputElement>) {
    updateAssetTag(event.target.value);
  }

  function addNewItem() {
    openModel("add");
    updatePageAction({
      type: "add",
    });
    updateAssetCateform(__NEW_ASSET_CATE_DEFAULT_VALUE);
  }

  async function openDeleteModel(item: TRowItem) {
    actionDialogStore.open({
      title: "DELETE ASSET CATEGORY",
      subtitle: `Once you delete ${item.name}, all related information will be lost forever. Are you sure you want to proceed?`,
      onRightBtnPress: handleDeleteRequest,
      type: "DELETE",
    });

    async function handleDeleteRequest() {
      if (!item.id) return;

      const res = await AssetCategoryApi.deleteAssetCategory(item.id);

      if (res.success) {
        setDataset((prev) => prev.filter((row) => row.id !== item.id));
        updatePaginationInitState(undefined);
        await fetchData();
      }

      alertDialogStore.open({
        title: res.success
          ? "Delete Asset Category"
          : "Delete Asset Category Failed",
        subtitle: res.message,
        isSuccess: res.success,
      });
    }
  }

  function clearSearch() {
    setPageState("empty-db");
  }

  const fetchCategoryTags = async (id: string) => {
    const tags = await AssetCategoryApi.getAssetCategoryTags(id);
    if (tags.data) {
      const mappedTags = tags.data.map((item) => ({
        name: item.name,
        id: item.asset_category_tag_id,
      }));
      setPreviousTagsCart(mappedTags);

      const selectedTagNames = tags.data.map((item) => item.name);
      const tagToSelect = await AssetTagApi.getAssetTags({
        is_deleted: false,
        page_size: 1000,
      });
      if (tagToSelect.data) {
        const preSelectedTags = tagToSelect.data.filter((tag) =>
          selectedTagNames.includes(tag.name),
        );
        setSelectedAssetTags(preSelectedTags as TRowItem[]);
      }
    }
  };

  const AddCategoryTagComponent = ({ item }: { item: TRowItem }) => {
    const handleTagAction = async () => {
      setSelectedAssetTags([]);
      updateAssetTagList({ data: [] });

      updatePageAction((p) => ({
        ...p,
        title: "Add Tags",
        id: item.id,
        name: item.name,
      }));
      isCategoryTagForm.current = true;
      openModel("add");
      await fetchCategoryTags(item.id);
    };
    return (
      <Box>
        <BadgeLabel onClick={handleTagAction} title={"Tags"} colorType="blue" />
      </Box>
    );
  };

  const handleModelClose = () => {
    setPreviousTagsCart([]);
    closeModel();
    updatePageAction({});
    updateAssetTag("");
    setSelectedAssetTags([]);
    isCategoryTagForm.current = false;
  };

  const renderHeaderDropDownMenu = ({
    closeDialog,
  }: {
    closeDialog: () => void;
  }) => {
    const batchDeleteCallback = async () => {
      resetSelectedRowItems();
      await fetchData();
      closeDialog();
    };
    return (
      <Stack direction={"column"} alignItems={"flex-start"} spacing={1}>
        <BulkDeleteController
          selectedRowItems={selectedRowItems}
          reset={batchDeleteCallback}
          ftn={AssetCategoryApi.deleteAssetCategory}
          idKey={"id"}
        />
      </Stack>
    );
  };

  const handleDeleteCatTag = (tag: IAssetTag) => () => {
    actionDialogStore.open({
      title: `Delete Asset Category Tag ${tag.name} ?`,
      subtitle: "Are you sure you want to delete this asset category tag?",
      onRightBtnPress: () => deleteTagRequest(tag.id),
      rightBtnTxt: "Continue",
      type: "DELETE",
    });

    const deleteTagRequest = async (tag_id: string) => {
      const res = await AssetCategoryApi.deleteAssetCategoryTag(
        pageAction?.id ?? "",
        tag_id,
      );
      alertDialogStore.open({
        title: res.success
          ? "Delete Asset Category Tag"
          : "Delete Asset Category Tag Failed",
        subtitle: res.message,
        isSuccess: res.success,
      });
      if (res.success && pageAction?.id) {
        await fetchCategoryTags(pageAction?.id);
        updateAssetTag("");
      }
    };
  };

  function handleUpdateModel(item: TRowItem) {
    openModel("add");
    updatePageAction({
      id: item.id,
      name: item.batch_number,
      type: "edit",
      data: item,
    });
    updateAssetCateform({
      asset_type: { value: item.asset_type, isValid: true },
      name: { value: item.name, isValid: true },
    });
  }

  return (
    <MainPageBase
      title={"Asset Categories"}
      subtitle={""}
      pageState={pageState}
      addItemBtnOnClick={addNewItem}
      addItemBtnText={"Add Asset Category"}
    >
      <Box
        height={`100%`}
        display={"flex"}
        alignItems={pageState !== "main" ? "center" : ""}
        justifyContent={"center"}
      >
        {pageState !== "main" ? (
          <NoItemContent
            type={pageState}
            title={page_info.title}
            subtitle={page_info.message}
            addItemBtn={addNewItem}
            clearSearchBtn={clearSearch}
          />
        ) : (
          <Stack width={"100%"} spacing={1}>
            <AssetCategoryFilter<AssetCategoryDetail[]>
              ftn={AssetCategoryApi.getAssetCategories}
              updateDataSet={populateDataSet}
              updateQuerySet={setQuerySet}
              loading={loading}
            />
            <DamsTable
              tableTitle="Asset Categories"
              tableSubtitle=""
              numTableRecords="25"
              tableHeaders={columns}
              tableKeys={tkeys}
              hiddenKeys={hkeys}
              hiddenLabels={hcolumns}
              data={dataset}
              maxRowCells={3}
              onTableItemDelete={openDeleteModel}
              extraRowComponent={AddCategoryTagComponent}
              useTableCheckbox
              onTableItemSelect={toggleRow}
              onTableItemEdit={handleUpdateModel}
              toggleSelectAllRow={toggleSelectAllRow}
              selectedRowItems={selectedRowItems}
              hasPagination
              paginationState={pagination}
              onNextPage={paginateToNextPage(querySet, populateDataSet)}
              onPreviousPage={paginateToPreviousPage(querySet, populateDataSet)}
              setActivePage={paginateToPageNumber(populateDataSet, querySet)}
              headerDropDownMenu={renderHeaderDropDownMenu}
            />

            {/* Add Asset Category Modal */}
            <ModalBase
              open={models.showAddModel}
              title={`${
                pageAction?.type === "edit" ? "Update" : ""
              } Asset Category Form`}
              subtitle={""}
              type={"add-item"}
              showHeader={true}
              leftIcon={DamsAppAsset.icons.devices}
              leftBtnTxt="Cancel"
              onLeftBtnPress={handleModelClose}
              rightBtnTxt={`${
                pageAction?.type === "edit" ? "Update" : "Add"
              } Category`}
              onRightBtnPress={handleCategoryRequest}
              onRightBtnLoading={isProcessing}
              onClose={handleModelClose}
            >
              <Stack gap={2}>
                <ItemSelectInput
                  size="full"
                  label={"Category Type"}
                  item={""}
                  placeholder={"Select Asset type"}
                  items={ASSET_CATE_TYPE_ITEM_LIST}
                  handleChange={handleSelectUpdate("asset_type")}
                  value={AssetCateform.asset_type?.value ?? ""}
                  error={AssetCateFormHasError("asset_type")}
                  errorMessage={AssetCateform.asset_type?.errorMessage}
                />
                <ModalItemInput
                  label="Asset Category"
                  placeholder="Enter Asset Category Name"
                  handleChange={handleAssetCateInputUpdate("name")}
                  value={AssetCateform.name.value}
                  error={AssetCateFormHasError("name")}
                  errorMessage={AssetCateform.name?.errorMessage}
                />
              </Stack>
            </ModalBase>

            {/* Add Asset Category Tag Modal */}
            <ModalBase
              key={pageAction?.id || "new"}
              width={500}
              open={models.showAddModel && isCategoryTagForm.current}
              title={`${pageAction?.name} Category Tags`}
              subtitle={"Manage Asset Tags"}
              type={"add-item"}
              showHeader={true}
              leftIcon={DamsAppAsset.icons.devices}
              leftBtnTxt="Cancel"
              onLeftBtnPress={handleModelClose}
              rightBtnTxt="Continue"
              onRightBtnPress={handleAddAssetTagRequest}
              onRightBtnLoading={isProcessing}
              onClose={handleModelClose}
            >
              <SimpleLabel
                title={
                  previousTagsCart.length === 0
                    ? "No tags added yet"
                    : "Current Tags"
                }
              />
              <Box minHeight={50}>
                {previousTagsCart.map((tag, index) => (
                  <BadgeLabel
                    key={index}
                    margin={0.5}
                    title={tag.name}
                    colorType="green"
                    isDeleteAction
                    onClick={handleDeleteCatTag(tag)}
                  />
                ))}
              </Box>
              <Stack gap={2}>
                <AssetTagSelectInput
                  size="full"
                  placeholder="Select Asset Tags"
                  label="Asset Tags"
                  handleChange={handleAssetTagSelect}
                  value={selectedAssetTags}
                  error={
                    selectedAssetTags.length === 0 && AssetTagList.submitAttempt
                  }
                />
              </Stack>
              <Box minHeight={50} marginTop={3}>
                {selectedAssetTags.map((tag, index) => (
                  <BadgeLabel
                    key={index}
                    margin={0.5}
                    onClick={() => {
                      setSelectedAssetTags((prev) =>
                        prev.filter((t) => t.id !== tag.id),
                      );
                      updateAssetTagList((prev) => ({
                        ...prev,
                        data: prev.data.filter((name) => name !== tag.name),
                      }));
                    }}
                    title={tag.name}
                    colorType="blue"
                    isDeleteAction
                  />
                ))}
              </Box>
            </ModalBase>
          </Stack>
        )}
      </Box>
    </MainPageBase>
  );
}

const getEmptyPageInfo = (key: string): { title: string; message: string } => {
  if (page_msg.hasOwnProperty(key)) {
    return page_msg[key];
  } else {
    return { title: "", message: "" };
  }
};

const page_msg: { [key: string]: { title: string; message: string } } = {
  "empty-db": {
    title: "No Asset Categories Found",
    message:
      'No Asset Categories have been added yet. Click the "Add New" button to add a new Asset Category.',
  },
  "empty-search": {
    title: "No Asset Categories Found",
    message:
      "No Asset Categories found in your database. Please add a new Asset Category to find them in the Asset Categories tab ",
  },
};

export default AssetCategoriesMainPage;

const ASSET_CATE_TYPE_ITEM_LIST = [
  { label: "Physical", value: "physical" },
  { label: "Non-Physical", value: "non-physical" },
];

const tkeys = ["name", "asset_type", "created_at"];
const columns = ["Asset Category Name", "Asset Category Type", "Date Added"];

const hkeys = ["asset_category_tag_names"];
const hcolumns = ["Category Tags"];
